// The audit of the write (#262, C5; `end-state-mockup.md` screen 5): the patch, the
// eligibility ruling from the resource schema, the CloudTrail attribution, the
// whole-resource diff, and the outcome read after the alarm's period rather than
// at the moment of the write.
//
// Written to `GET /api/v1/changes/{id}/audit` (`api/changeAudit.ts`), and shown on
// the run page beside the change once the platform has written it.

import {
  wholeDocumentDiff,
  type ChangeAudit as Audit,
  type StoredDocument,
} from "../api/changeAudit";
import { ChangeProgress, useChangeAudit } from "./ChangeProgress";
import { Chip, Notice } from "./chrome";
import { readableTime as time } from "./runPresentation";

const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const show = (value: unknown): string => (value === undefined ? "(absent)" : JSON.stringify(value));

export function ChangeAudit({ changeId }: { changeId: string }) {
  const { audit, error } = useChangeAudit(changeId);
  if (error !== null) return <Notice tone="bad">The audit of this change is unavailable: {error}</Notice>;
  if (audit === null) return <p role="status">Reading the audit of the write…</p>;
  return <ChangeAuditView audit={audit} />;
}

// What a person is told of a write that did not happen. A lapsed approval (#915) is said
// in the sentence the executor recorded, which names the organisation: what happened, and
// what to do. Its reason's code is the system's word for it and is shown to nobody. The
// second sentence here is the same statement with no name, for a row that recorded none.
const LAPSED =
  "This change was approved by a member of a partner while this account was shared with " +
  "that partner. That sharing was interrupted before the change was applied, so the change " +
  "was not applied. To apply it, the change must be proposed and approved again.";

// An approval its account's disconnect cancelled (#1123), said the same way: the sentence the
// disconnect recorded, or this one where it recorded none.
const CANCELLED =
  "This change was approved, but its AWS account was disconnected from Torque Assist before the " +
  "change was applied, so the approval was cancelled and Torque Assist will not apply the change. " +
  "If the account is connected again, the change must be proposed and approved again.";

const SAID: ReadonlyMap<string, string> = new Map([
  ["approval_lapsed", LAPSED],
  ["approval_cancelled", CANCELLED],
]);

// A write that may have reached an account which was then disconnected (#1121): the sentence
// the executor recorded says the outcome is unknown, so it is shown as it is and never under
// "Not applied". The same sentence stands in where none was recorded.
const NOT_KNOWN =
  "This change's AWS account was disconnected from Torque Assist while the change was being " +
  "written. Torque Assist can no longer read the account, so whether the change was applied is unknown.";

const isUnknown = (failed: Record<string, unknown>): boolean =>
  failed.reason === "account_not_active" && failed.write_attempted === true;

function notApplied(failed: Record<string, unknown>): string {
  if (isUnknown(failed)) {
    return typeof failed.detail === "string" && failed.detail !== "" ? failed.detail : NOT_KNOWN;
  }
  const plain = typeof failed.reason === "string" ? SAID.get(failed.reason) : undefined;
  if (plain !== undefined) {
    return typeof failed.detail === "string" && failed.detail !== "" ? failed.detail : plain;
  }
  return `Not applied: ${String(failed.reason)}${failed.detail ? ` — ${String(failed.detail)}` : ""}`;
}

function phase(audit: Audit, name: string): Record<string, unknown> | null {
  return audit.phases.find((p) => p.phase === name)?.payload ?? null;
}

export function ChangeAuditView({ audit }: { audit: Audit }) {
  const change = record(audit.request.resource_change);
  const patch = Array.isArray(change.patch) ? change.patch : [];
  const eligibility = phase(audit, "eligibility");
  const failed = phase(audit, "failed");
  const outcome = phase(audit, "outcome");
  const attribution = audit.attribution ?? {};
  return (
    <section className="stack stack--tight change-audit" aria-label="The audit of the write">
      <h3>
        The audit of the write <Chip tone={audit.status === "applied" ? "good" : audit.status === "failed" ? "bad" : undefined}>{audit.status}</Chip>
      </h3>
      <ChangeProgress audit={audit} />

      <section aria-label="Patch">
        <h4>Patch</h4>
        <p>
          {String(change.resource_type ?? "")} <code>{String(change.identifier ?? "")}</code> in{" "}
          {String(change.region ?? "")}
        </p>
        <pre className="investigation-json">{JSON.stringify(patch, null, 2)}</pre>
      </section>

      <section aria-label="Eligibility">
        <h4>Eligibility from the resource schema</h4>
        {eligibility === null ? (
          <p>AWS was not asked.</p>
        ) : (
          <>
            <p>{eligibility.eligible === true ? "Eligible when requested." : "Not eligible when requested."}</p>
            {Array.isArray(eligibility.properties)
              ? eligibility.properties.map((raw) => {
                  const p = record(raw);
                  return (
                    <p key={String(p.root_property)}>
                      <code>{String(p.root_property)}</code>: schema {String(p.schema)}, policy{" "}
                      {String(p.mode)}
                    </p>
                  );
                })
              : null}
          </>
        )}
        {failed !== null && failed.reason === "not_eligible" ? (
          <p>Ruled again at the write and refused: {(failed.eligibility_reasons as string[] | undefined)?.join(", ")}</p>
        ) : null}
      </section>

      <section aria-label="Attribution">
        <h4>CloudTrail attribution</h4>
        <Approval approval={audit.approval} />
        {typeof attribution.source_identity === "string" ? (
          <>
            <p>
              Written as <code>{attribution.role_arn as string}</code> with source identity{" "}
              <code>{attribution.source_identity}</code>, session{" "}
              <code>{String(attribution.role_session_name ?? "")}</code>.
            </p>
            <p className="muted">
              In account {String(attribution.account_id ?? "")}'s CloudTrail, look for{" "}
              <code>sessionContext.sourceIdentity = {attribution.source_identity}</code>
              {attribution.request_token ? (
                <>
                  {" "}and Cloud Control request <code>{String(attribution.request_token)}</code>
                </>
              ) : null}
              .
            </p>
          </>
        ) : (
          <p>No session was opened in the account: nothing was written there.</p>
        )}
        {audit.executed_at ? <p>Written at {time(audit.executed_at)}.</p> : null}
        {failed !== null ? <Notice tone="bad">{notApplied(failed)}</Notice> : null}
      </section>

      <section aria-label="Diff">
        <h4>Whole-resource diff</h4>
        <ResourceDiff verification={audit.verification} unknown={failed !== null && isUnknown(failed)} />
      </section>

      <section aria-label="Outcome">
        <h4>Outcome</h4>
        <Outcome audit={audit} outcome={outcome} unknown={failed !== null && isUnknown(failed)} />
      </section>
    </section>
  );
}

function Approval({ approval }: { approval: Audit["approval"] }) {
  if (approval.approver === null) return <p>No one has approved this change.</p>;
  // A name with no address is all the route had to send (a purged approver, #1135): it is
  // shown as sent, with nothing put beside it.
  const { display_name: name, email, user_id } = approval.approver;
  const who = name ? (email ? `${name} (${email})` : name) : (email ?? user_id);
  const authority =
    approval.authority === "grant"
      ? `a partner's access to approve writes${approval.grant_id ? ` (${approval.grant_id})` : ""}`
      : "the workspace's own members";
  return (
    <dl className="investigation-facts">
      <dt>Approved by</dt>
      <dd>
        {who}
        {approval.approved_at ? ` at ${time(approval.approved_at)}` : ""}
      </dd>
      <dt>Factor</dt>
      <dd>
        {approval.step_up_method || "Unknown"}
        {approval.assurance_id ? (
          <>
            {" "}· assurance <code>{approval.assurance_id}</code>
          </>
        ) : null}
      </dd>
      <dt>Authority</dt>
      <dd>{authority}</dd>
    </dl>
  );
}

function unavailable(side: string, stored: StoredDocument): string | null {
  if (stored.status === "too_large") return `The resource ${side} the write was too large to keep (${stored.bytes} bytes); it was not truncated.`;
  if (stored.status === "unreadable") return `The resource could not be read ${side} the write${stored.reason ? `: ${stored.reason}` : ""}.`;
  return null;
}

function ResourceDiff({ verification, unknown }: { verification: Audit["verification"]; unknown: boolean }) {
  if (verification === null) {
    return <p>{unknown ? "The resource was not read after the write." : "The resource was not read."}</p>;
  }
  const gaps = [unavailable("before", verification.before), unavailable("after", verification.after)].filter(
    (gap): gap is string => gap !== null,
  );
  if (gaps.length) return <>{gaps.map((gap) => <p key={gap}>{gap}</p>)}</>;
  const rows = wholeDocumentDiff(
    verification.before.document ?? {},
    verification.after.document ?? {},
    verification.roots,
  );
  if (rows.length === 0) return <p>No property of the resource differs between before and after.</p>;
  return (
    <table className="change-audit__diff">
      <thead>
        <tr>
          <th>Property</th>
          <th>Before</th>
          <th>After</th>
          <th>In the patch</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.property}>
            <td>
              <code>{row.property}</code>
            </td>
            <td>{show(row.before)}</td>
            <td>{show(row.after)}</td>
            <td>{row.patched ? "patched" : "not in the patch"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Outcome({ audit, outcome, unknown }: { audit: Audit; outcome: Record<string, unknown> | null; unknown: boolean }) {
  if (outcome !== null) {
    if (outcome.observed === true) {
      return (
        <p>
          Alarm <code>{String(outcome.alarm_name)}</code> read {String(outcome.state)} at{" "}
          {time(String(outcome.read_at))}, after its period ended at {time(String(outcome.due_at))}
          {outcome.resolved === true ? " — resolved." : " — not resolved."}
        </p>
      );
    }
    return <p>Not observed: {String(outcome.reason)}{outcome.detail ? ` — ${String(outcome.detail)}` : ""}.</p>;
  }
  if (audit.outcome_due_at) {
    return <p>Read after the alarm's period, not at the write: due at {time(audit.outcome_due_at)}.</p>;
  }
  if (unknown) return <p>No outcome is read: whether the change was applied is not known.</p>;
  return <p>No outcome is read for a change that was not applied.</p>;
}
